(() => {
  const initialized = new WeakSet();
  const activePanels = new Map();

  function initialize(root) {
    if (initialized.has(root)) return;

    const tabs = Array.from(root.querySelectorAll('[role="tab"]'));
    const panels = tabs.map((tab) => document.getElementById(tab.getAttribute('aria-controls')));
    if (!tabs.length || panels.some((panel) => !panel)) return;

    initialized.add(root);
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    const sectionKey = root.dataset.sectionId || tabs[0].id;
    const isEditor = root.dataset.editorPreview === 'true';
    const storageKey = `jewelpa-detail-panel:${sectionKey}:${root.dataset.productId}`;
    let savedPanel = activePanels.get(sectionKey);
    if (isEditor) {
      try {
        savedPanel = sessionStorage.getItem(storageKey) || savedPanel;
      } catch {
        // Storage can be unavailable in a restricted preview frame.
      }
    }
    let activeIndex = -1;
    let panelAnimation;

    const activate = (index, moveFocus = false) => {
      if (index === activeIndex) {
        if (moveFocus) tabs[index].focus();
        return;
      }

      const previousIndex = activeIndex;
      panelAnimation?.cancel();
      tabs.forEach((tab, tabIndex) => {
        const selected = tabIndex === index;
        tab.setAttribute('aria-selected', String(selected));
        tab.tabIndex = selected ? 0 : -1;
        panels[tabIndex].hidden = !selected;
      });

      activeIndex = index;
      activePanels.set(sectionKey, panels[index].id);
      if (isEditor) {
        try {
          sessionStorage.setItem(storageKey, panels[index].id);
        } catch {
          // The in-memory selection still survives section-only refreshes.
        }
      }
      if (previousIndex >= 0 && !reduceMotion.matches && panels[index].animate) {
        const offset = index > previousIndex ? 24 : -24;
        panelAnimation = panels[index].animate(
          [
            { opacity: 0, transform: `translateX(${offset}px)` },
            { opacity: 1, transform: 'translateX(0)' },
          ],
          { duration: 280, easing: 'cubic-bezier(0.22, 1, 0.36, 1)' },
        );
      }

      if (moveFocus) tabs[index].focus();
    };

    tabs.forEach((tab, index) => {
      tab.addEventListener('click', () => activate(index));
      tab.addEventListener('keydown', (event) => {
        let next;
        if (event.key === 'ArrowRight') next = (index + 1) % tabs.length;
        else if (event.key === 'ArrowLeft') next = (index - 1 + tabs.length) % tabs.length;
        else if (event.key === 'Home') next = 0;
        else if (event.key === 'End') next = tabs.length - 1;
        else return;

        event.preventDefault();
        activate(next, true);
      });
    });

    root.addEventListener('shopify:block:select', (event) => {
      const panelIndex = panels.findIndex((panel) => panel.contains(event.target));
      if (panelIndex >= 0) activate(panelIndex);
    });

    const linkedIndex = panels.findIndex((panel) => `#${panel.id}` === window.location.hash);
    const savedIndex = panels.findIndex((panel) => panel.id === savedPanel);
    activate(linkedIndex >= 0 ? linkedIndex : savedIndex >= 0 ? savedIndex : 0);
  }

  const initializeAll = () => {
    document.querySelectorAll('[data-custom-product-anchor-details]').forEach(initialize);
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initializeAll, { once: true });
  else initializeAll();
  document.addEventListener('shopify:section:load', initializeAll);

  // Reuse Horizon's variant selection result; no extra request is needed for SKU changes.
  document.addEventListener('shopify:product:select', (event) => {
    event.promise?.then(({ detail }) => {
      if (!detail) return;
      document.querySelectorAll('[data-custom-product-anchor-details]').forEach((root) => {
        if (root.dataset.productId !== String(detail.productId)) return;
        root.querySelectorAll('[data-specification-sku]').forEach((cell) => {
          cell.textContent = detail.resource?.sku || cell.dataset.emptyValue;
        });
      });
    }).catch((error) => {
      if (error?.name !== 'AbortError') console.warn('[product specifications]', error);
    });
  });
})();
